// Shared, framework-agnostic SEO helpers. Pure functions only (no Nuxt context), so each is
// independently unit-testable and reusable across pages/composables that build head metadata
// and structured data for public pages.

const META_DESCRIPTION_MAX = 160;

/**
 * Remove the "| Anyway" brand suffix that the global title template re-appends. CMS SEO titles
 * are authored with the brand already included, so it must be stripped before the template adds
 * it again — otherwise the tab/title reads "… | Anyway | Anyway". Kept here (not inline) so it
 * stays coupled to the one title template that owns the suffix.
 */
export const stripBrandSuffix = (title: string | null | undefined): string =>
  (title ?? '').replace(/\s*\|\s*Anyway\s*$/i, '');

const HTML_ENTITIES: Record<string, string> = { 'amp': '&', 'lt': '<', 'gt': '>', 'quot': '"', 'apos': '\'', 'nbsp': ' ', '#39': '\'' };

/**
 * Plain text from an authored HTML fragment (e.g. `<p>…</p><p>…</p>`), for head tags that take
 * text: tags become word breaks, whitespace collapses, and the entities an editor emits decode
 * so the head renderer doesn't escape them a second time.
 */
export const htmlToPlainText = (html: string): string =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(amp|lt|gt|quot|apos|nbsp|#39);/g, (_, entity: string) => HTML_ENTITIES[entity] ?? '')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Truncate a meta description to the 160-char meta limit, cutting on a word boundary when one
 * exists (a whitespace-free run is cut mid-word) and appending an ellipsis. Returns the text
 * unchanged when it is already within the limit.
 */
export const truncateMetaDescription = (text: string): string =>
  text.length > META_DESCRIPTION_MAX
    ? `${text.slice(0, META_DESCRIPTION_MAX).replace(/\s+\S*$/, '')}…`
    : text;

/**
 * Build a schema.org `Occupation` node for a public job page. Generic structured data for
 * crawlers and AI engines — Google's estimated-salary rich result is not claimed (it additionally
 * requires a `mainEntityOfPage` with a `lastReviewed` date, which occupations don't carry).
 * `estimatedSalary` (median annual wage) and `occupationLocation` (the cluster's country) are
 * emitted only when available, since a partial node is still valid schema.org.
 */
export const buildOccupationSchema = (input: {
  url: string;
  name: string;
  description: string;
  skills?: string[];
  /** Median annual wage with its ISO-4217 currency. */
  medianAnnualWage?: { amount: number; currency: string };
  /** Country the occupation/wage data applies to (the cluster's region). */
  countryName?: string;
}) => ({
  '@type': 'Occupation',
  '@id': `${input.url}#occupation`,
  'url': input.url,
  'name': input.name,
  'description': input.description,
  ...(input.skills?.length ? { skills: input.skills.join(', ') } : {}),
  ...(input.medianAnnualWage
    ? {
        estimatedSalary: [{
          '@type': 'MonetaryAmountDistribution',
          'name': 'base',
          'currency': input.medianAnnualWage.currency,
          'duration': 'P1Y',
          'median': input.medianAnnualWage.amount,
        }],
      }
    : {}),
  ...(input.countryName ? { occupationLocation: [{ '@type': 'Country', 'name': input.countryName }] } : {}),
});

/**
 * Build a schema.org `Course` node (with a nested `CourseInstance`) for a public learn resource
 * — a collection or an individual asset. `workloadMinutes` is emitted as an ISO-8601 duration
 * (`PT{n}M`) and omitted when zero/absent; a missing `description` is likewise omitted. The pages
 * pass the resolved values and feed the result to `useSchemaOrg` so the JSON-LD renders in the
 * initial SSR HTML for non-JS crawlers and AI engines.
 */
/**
 * Build a schema.org `Article` node for a public article page. Built from published content
 * only — preview/draft copy never enters structured data. Optional fields are omitted when
 * absent, since a partial node is still valid schema.org.
 */
export const buildArticleSchema = (input: {
  url: string;
  headline: string;
  description?: string;
  imageUrl?: string;
  datePublished?: string;
  dateModified?: string;
  authorName?: string;
}) => ({
  '@type': 'Article',
  '@id': `${input.url}#article`,
  'url': input.url,
  'mainEntityOfPage': input.url,
  'headline': input.headline,
  ...(input.description ? { description: input.description } : {}),
  ...(input.imageUrl ? { image: input.imageUrl } : {}),
  ...(input.datePublished ? { datePublished: input.datePublished } : {}),
  ...(input.dateModified ? { dateModified: input.dateModified } : {}),
  ...(input.authorName ? { author: { '@type': 'Person', 'name': input.authorName } } : {}),
});

/**
 * Build a schema.org `VideoObject` node for a public video page. `embedUrl` is the provider's
 * player URL the page iframes, not the authored watch URL. Optional fields are omitted when
 * absent, since a partial node is still valid schema.org.
 */
export const buildVideoSchema = (input: {
  url: string;
  name: string;
  description?: string;
  thumbnailUrl?: string;
  uploadDate?: string;
  embedUrl?: string;
}) => ({
  '@type': 'VideoObject',
  '@id': `${input.url}#video`,
  'url': input.url,
  'name': input.name,
  ...(input.description ? { description: input.description } : {}),
  ...(input.thumbnailUrl ? { thumbnailUrl: input.thumbnailUrl } : {}),
  ...(input.uploadDate ? { uploadDate: input.uploadDate } : {}),
  ...(input.embedUrl ? { embedUrl: input.embedUrl } : {}),
});

/**
 * Build a schema.org `FAQPage` node mirroring an article's rendered FAQ section. Only visible
 * Q&A belongs in structured data, so callers pass plain-text answers already resolved from
 * rich text. Returns null when no complete pairs exist — an absent node beats an empty one.
 */
export const buildFaqPageSchema = (input: {
  url: string;
  faqs: { question: string; answer: string }[];
}) => {
  if (input.faqs.length === 0) {
    return null;
  }

  return {
    '@type': 'FAQPage',
    '@id': `${input.url}#faq`,
    'url': input.url,
    'mainEntity': input.faqs.map(faq => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': { '@type': 'Answer', 'text': faq.answer },
    })),
  };
};

export const buildCourseSchema = (input: {
  url: string;
  name: string;
  description?: string;
  workloadMinutes?: number;
  /** @id of the sitewide Organization (`#identity`) to reference as the provider. */
  providerId: string;
}) => {
  // Google requires `description` for the Course rich result. When the page has none, emit no
  // node at all — an absent node beats a node that fails validation.
  if (!input.description) {
    return null;
  }

  return {
    '@type': 'Course',
    '@id': `${input.url}#course`,
    'url': input.url,
    'name': input.name,
    'description': input.description,
    // Reference the sitewide Organization by @id (single-sourced brand identity with its
    // sameAs/legalName) rather than emitting a second anonymous org node per page.
    'provider': { '@id': input.providerId },
    'isAccessibleForFree': true,
    'hasCourseInstance': {
      '@type': 'CourseInstance',
      'courseMode': 'online',
      ...(input.workloadMinutes ? { courseWorkload: `PT${input.workloadMinutes}M` } : {}),
    },
  };
};
